import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useAssentStatus from '../../hooks/useAssentStatus';
import { getCollegeList, getFitCandidates, addCollegeAnalysis, getUniversityBranding } from '../../services/api';
import { PROGRESS_EVENT } from '../../services/progressEvent';
import { track, EVENTS } from '../../services/analytics';
import { onboardingLandingPath } from '../../utils/onboardingLanding';
import { composeStartingPicture, DIVE_GRACE_MS } from '../../utils/startingPicture';
import { ASTRA_PATH } from '../../utils/astraLink';
import { shortUniversityName } from '../../utils/formatUniversityName';
import { Crest } from '../UniversityCrest';
import StartingWindow from './StartingWindow';

// The first-read bar (#2359, founder pick C 2026-09-29). Half the students
// who built a profile never added a college, and they come back to eight
// different pages, so the comeback lives in the app shell: "Your full read on
// UCLA", the first school their /welcome starting picture chose, on every
// signed-in page until the list holds a school. Start runs the shipped
// one-tap dive (3-second window with Cancel); Not now hides it for 7 days.
//
// Phone: docked at the bottom, and Astra's launcher lifts to clear it (the
// mock's real render: the two collide otherwise). The lift is the bar's
// MEASURED height in --first-read-bar-h, which the launcher and the page's
// bottom padding read, so a taller bar (the start window) lifts them too.
// Wider screens: a one-line banner at the top of the content.

export const FIRST_READ_SURFACE = 'bar';
// On the onboarding_dive_* events, which /welcome also fires without a
// surface, so the two funnels stay separable.
export const FIRST_READ_DIVE_SURFACE = 'first_read_bar';
export const FIRST_READ_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
export const BAR_HEIGHT_VAR = '--first-read-bar-h';
const DOCK_QUERY = '(max-width: 639px)';

export const doneKey = (email) => `first_read_bar_done_${email}`;
export const dismissedKey = (email) => `first_read_bar_dismissed_${email}`;

const readLocal = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const writeLocal = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

export function isSnoozed(email, now = Date.now()) {
    const at = Number(readLocal(dismissedKey(email)));
    return Number.isFinite(at) && at > 0 && now - at < FIRST_READ_SNOOZE_MS;
}

// Where the bar may show. /welcome and /pricing sit outside the app shell;
// Astra's own page is excluded here. (The admin pages, once excluded too,
// live on the admin portal since #5831, which never mounts the bar.)
const barAllowedOn = (pathname) => pathname !== ASTRA_PATH;

// The page an impression happened on: the first path segment only, never an
// id (an essay page's path carries a university id).
const pageOf = (pathname) => String(pathname || '').split('/')[1] || 'home';

function useDocked() {
    const get = () => {
        try { return typeof window !== 'undefined' && window.matchMedia?.(DOCK_QUERY).matches === true; } catch { return false; }
    };
    const [docked, setDocked] = useState(get);
    useEffect(() => {
        let mq;
        try { mq = window.matchMedia?.(DOCK_QUERY); } catch { mq = null; }
        if (!mq?.addEventListener) return undefined;
        const on = () => setDocked(mq.matches);
        mq.addEventListener('change', on);
        return () => mq.removeEventListener('change', on);
    }, []);
    return docked;
}

export default function FirstReadBar() {
    const { currentUser } = useAuth();
    const email = currentUser?.email || null;
    const assented = useAssentStatus(email) === 'assented';
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const docked = useDocked();

    const [done, setDone] = useState(() => (email ? readLocal(doneKey(email)) === 'true' : false));
    const [snoozed, setSnoozed] = useState(() => (email ? isSnoozed(email) : false));
    const [listEmpty, setListEmpty] = useState(false);
    const [pick, setPick] = useState(null);
    const [branding, setBranding] = useState(null);
    // idle -> starting (the 3s window) -> running -> (navigates) | failed
    const [phase, setPhase] = useState('idle');
    const timer = useRef(null);
    const inFlight = useRef(false);
    const phaseRef = useRef(phase);
    phaseRef.current = phase;

    useEffect(() => {
        setDone(email ? readLocal(doneKey(email)) === 'true' : false);
        setSnoozed(email ? isSnoozed(email) : false);
        setPick(null);
        setBranding(null);
        setListEmpty(false);
    }, [email]);

    const wanted = Boolean(email && assented && !done && !snoozed);
    // A read that lands after the account changed belongs to someone else.
    const emailRef = useRef(email);
    emailRef.current = email;

    // Is the list still empty? Re-read on every page change and whenever
    // any surface reports new progress (an add from Discover), so the bar
    // leaves the moment a school lands. A failed read is NOT "empty": the
    // bar stays hidden rather than nag a student who may have a list.
    const checkList = useCallback(() => {
        if (!wanted) return undefined;
        let alive = true;
        getCollegeList(email).then((res) => {
            if (!alive || emailRef.current !== email) return;
            const list = res?.success !== false && Array.isArray(res?.college_list) ? res.college_list : null;
            if (list && list.length > 0) {
                writeLocal(doneKey(email), 'true');
                setDone(true);
                setListEmpty(false);
            } else {
                setListEmpty(Array.isArray(list));
            }
        }).catch(() => { if (alive) setListEmpty(false); });
        return () => { alive = false; };
    }, [wanted, email]);
    useEffect(() => checkList(), [checkList, pathname]);
    useEffect(() => {
        if (!wanted) return undefined;
        const on = () => { checkList(); };
        window.addEventListener(PROGRESS_EVENT, on);
        return () => window.removeEventListener(PROGRESS_EVENT, on);
    }, [wanted, checkList]);

    // The starting picture, composed exactly as /welcome composes it, once per
    // student. No candidates (no profile yet) means no bar.
    useEffect(() => {
        if (!wanted || !listEmpty || pick) return undefined;
        let alive = true;
        Promise.all([
            getFitCandidates(email, 'Reach', { limit: 6 }),
            getFitCandidates(email, 'Target', { limit: 4 }),
        ]).then(([r, t]) => {
            if (!alive || emailRef.current !== email) return;
            const first = composeStartingPicture(
                r?.success ? r.candidates : [], t?.success ? t.candidates : [])[0] || null;
            setPick(first);
        }).catch(() => {});
        return () => { alive = false; };
    }, [wanted, listEmpty, pick, email]);

    // The school's crest and colours, fetched for that one university.
    const pickId = pick?.university_id || null;
    useEffect(() => {
        if (!email || !pickId) return undefined;
        let alive = true;
        getUniversityBranding(email, [pickId])
            .then((b) => { if (alive) setBranding(b?.[pickId] || null); })
            .catch(() => {});
        return () => { alive = false; };
    }, [email, pickId]);

    const clearTimer = () => { if (timer.current) clearTimeout(timer.current); timer.current = null; };

    // Leaving the app shell inside the window (to /pricing, say) unmounts the
    // bar and clears the timer. That is a cancel, and it reports itself: the
    // silent version of this is what #2301 closed on /welcome.
    useEffect(() => () => {
        if (timer.current && phaseRef.current === 'starting') {
            track(EVENTS.ONBOARDING_DIVE_CANCELLED, { surface: FIRST_READ_DIVE_SURFACE, reason: 'left_page' });
        }
        clearTimer();
    }, []);

    const run = async (c) => {
        clearTimer();
        if (!c || inFlight.current) return;
        inFlight.current = true;
        setPhase('running');
        track(EVENTS.ONBOARDING_DIVE_CONFIRMED, { band: c.soft_fit_category, surface: FIRST_READ_DIVE_SURFACE });
        try {
            const res = await addCollegeAnalysis(email, c.university_id);
            if (!res?.success) throw new Error(res?.error || 'analysis failed');
            writeLocal(doneKey(email), 'true');
            setDone(true);
            setPhase('idle');
            navigate(onboardingLandingPath(c.university_id));
        } catch (err) {
            console.error('[FirstReadBar] deep dive failed:', err);
            track(EVENTS.ONBOARDING_DIVE_FAILED, { surface: FIRST_READ_DIVE_SURFACE });
            setPhase('failed');
        } finally {
            inFlight.current = false;
        }
    };

    const start = () => {
        if (!pick || inFlight.current) return;
        track(EVENTS.FIRST_READ_PROMPT_TAPPED, { surface: FIRST_READ_SURFACE, band: pick.soft_fit_category });
        clearTimer();
        setPhase('starting');
        const c = pick;
        timer.current = setTimeout(() => run(c), DIVE_GRACE_MS);
    };
    const cancel = () => {
        clearTimer();
        track(EVENTS.ONBOARDING_DIVE_CANCELLED, { surface: FIRST_READ_DIVE_SURFACE });
        setPhase('idle');
    };
    const notNow = () => {
        track(EVENTS.FIRST_READ_PROMPT_DISMISSED, { surface: FIRST_READ_SURFACE });
        writeLocal(dismissedKey(email), String(Date.now()));
        setSnoozed(true);
    };

    // Held on screen through a started dive even if the list read flips
    // (the add itself fills the list): the student must see it run.
    const busy = phase === 'starting' || phase === 'running';
    const visible = Boolean(pick) && barAllowedOn(pathname)
        && ((wanted && listEmpty) || (busy && !snoozed));

    // One impression per page the bar is shown on.
    const shownOn = useRef(null);
    useEffect(() => {
        if (!visible || shownOn.current === pathname) return;
        shownOn.current = pathname;
        track(EVENTS.FIRST_READ_PROMPT_SHOWN, { surface: FIRST_READ_SURFACE, page: pageOf(pathname) });
    }, [visible, pathname]);

    // The launcher lift: publish the docked bar's measured height.
    const barRef = useRef(null);
    const dockedVisible = visible && docked;
    useLayoutEffect(() => {
        const root = document.documentElement;
        const el = barRef.current;
        if (!dockedVisible || !el) {
            root.style.removeProperty(BAR_HEIGHT_VAR);
            return undefined;
        }
        const publish = () => root.style.setProperty(BAR_HEIGHT_VAR, `${Math.ceil(el.getBoundingClientRect().height)}px`);
        publish();
        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(publish);
            ro.observe(el);
        }
        return () => {
            ro?.disconnect();
            root.style.removeProperty(BAR_HEIGHT_VAR);
        };
    }, [dockedVisible, phase]);

    // Start swaps the Start button for the window: move focus into it (not
    // onto Start now, so a held key never starts the read), without scrolling.
    const windowRef = useRef(null);
    useEffect(() => {
        if (phase === 'starting') windowRef.current?.focus?.({ preventScroll: true });
    }, [phase]);

    if (!visible) return null;

    const name = shortUniversityName(pick.university_id, pick.university_name);
    let body;
    if (phase === 'starting') {
        body = (
            <StartingWindow ref={windowRef} pick={pick} onStartNow={() => run(pick)} onCancel={cancel}
                compact testIdPrefix="first-read" />
        );
    } else if (phase === 'running') {
        body = (
            <div className="flex items-center gap-3" role="status" data-testid="first-read-running">
                <span className="h-5 w-5 shrink-0 animate-spin motion-reduce:animate-none rounded-full border-2 border-[#D6E8D5] border-t-[#1A4D2E]" aria-hidden="true" />
                <p className="text-sm font-medium text-[#1A4D2E]">
                    Your deep dive on {name} is running. About a minute.
                </p>
            </div>
        );
    } else {
        body = (
            <div className="flex items-center gap-3">
                <Crest name={pick.university_name} branding={branding} size={36} />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#2C2C2C] truncate">Your full read on {name}</p>
                    {phase === 'failed' ? (
                        <p className="text-xs text-[#C0392B]" role="alert" data-testid="first-read-error">
                            We couldn&apos;t finish the read. Please try again in a moment.
                        </p>
                    ) : (
                        <p className="text-xs text-[#6B6B6B]">From your starting picture. About a minute.</p>
                    )}
                </div>
                <button type="button" onClick={start} data-testid="first-read-start"
                    className="shrink-0 min-h-[44px] rounded-xl bg-[#1A4D2E] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2D6B45]">
                    Start
                </button>
                <button type="button" onClick={notNow} data-testid="first-read-dismiss"
                    aria-label="Not now"
                    className="shrink-0 grid h-11 w-11 -mr-2 place-items-center rounded-full text-lg text-[#6B6B6B] hover:bg-white hover:text-[#2C2C2C] sm:w-auto sm:px-2 sm:text-sm sm:font-medium">
                    <span className="sm:hidden" aria-hidden="true">&times;</span>
                    <span className="hidden sm:inline">Not now</span>
                </button>
            </div>
        );
    }

    // z-30, under Astra's drawer (z-40): when she opens on a phone her
    // message box must never sit behind this bar.
    if (docked) {
        return (
            <div ref={barRef}
                className="fixed inset-x-0 bottom-0 z-30 border-t border-[#E0DED8] bg-[#FDFCF7]/95 backdrop-blur px-4 pt-3"
                style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
                data-testid="first-read-bar" data-placement="dock">
                {body}
            </div>
        );
    }
    return (
        <div className="mb-5 rounded-2xl border border-[#CFE3D4] bg-[#F0F7F1] px-4 py-3"
            data-testid="first-read-bar" data-placement="banner">
            {body}
        </div>
    );
}
